/**
 * Tenants Page - 테넌트 관리
 *
 * 테넌트(상위 조직) 목록을 조회/등록/수정/삭제하는 화면.
 * 사용자 관리(UsersPage)와 동일한 리스트 레이아웃.
 *
 * ※ 백엔드 API 연동은 2단계(테넌트 데이터 모델) 작업에서 연결합니다.
 *   현재는 로컬 상태 기반으로 UI 흐름만 동작합니다.
 */

import { useState, useEffect } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Button, Badge, Modal, SearchInput, SortableTh, useTableSort, useToast } from '../components/ui';
import { tenantApi, customerApi, Tenant } from '../api';
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';

export function TenantsPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customerCounts, setCustomerCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const sort = useTableSort<'name' | 'code' | 'description' | 'customerCount' | 'status' | 'createdAt'>();

  // 상세/수정 모달
  const [selected, setSelected] = useState<Tenant | null>(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', description: '', status: 'active' as 'active' | 'inactive' });
  const [saving, setSaving] = useState(false);

  // 등록 모달
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', code: '', description: '', status: 'active' as 'active' | 'inactive' });
  const [addErrors, setAddErrors] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const [data, allCustomers] = await Promise.all([
        tenantApi.list(),
        customerApi.list().catch(() => []),
      ]);
      setTenants(data);
      // 테넌트별 고객사 수 집계
      const counts: Record<string, number> = {};
      for (const c of allCustomers) {
        counts[c.tenantId] = (counts[c.tenantId] || 0) + 1;
      }
      setCustomerCounts(counts);
    } catch {
      showToast('error', '테넌트 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = tenants.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.name || '').toLowerCase().includes(q) ||
      (t.code || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  });

  const highlightText = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    const q = searchQuery.toLowerCase();
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="search-highlight">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const openDetail = (t: Tenant) => {
    setSelected(t);
    setEditForm({ name: t.name, code: t.code, description: t.description || '', status: t.status });
  };

  const closeDetail = () => {
    setSelected(null);
    setEditForm({ name: '', code: '', description: '', status: 'active' });
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!editForm.name.trim() || !editForm.code.trim()) {
      showToast('error', '이름과 코드는 필수입니다.');
      return;
    }
    try {
      setSaving(true);
      const updated = await tenantApi.update(selected.id, editForm);
      setTenants((prev) => prev.map((t) => (t.id === selected.id ? { ...t, ...updated } : t)));
      showToast('success', '테넌트 정보가 수정되었습니다.');
      closeDetail();
    } catch {
      showToast('error', '수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 등록 모달 닫기 — 입력값 + 에러 초기화
  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm({ name: '', code: '', description: '', status: 'active' });
    setAddErrors(new Set());
  };
  const clearAddError = (field: string) => {
    setAddErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const handleAdd = async () => {
    const errs = new Set<string>();
    if (!addForm.name.trim()) errs.add('name');
    if (!addForm.code.trim()) errs.add('code');
    if (errs.size > 0) { setAddErrors(errs); return; }
    setAddErrors(new Set());
    try {
      setAdding(true);
      const created = await tenantApi.create(addForm);
      setTenants((prev) => [...prev, created]);
      showToast('success', '테넌트가 등록되었습니다.');
      closeAddModal();
    } catch {
      showToast('error', '등록에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setDeleteConfirm({ id, name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await tenantApi.remove(deleteConfirm.id);
      setTenants((prev) => prev.filter((t) => t.id !== deleteConfirm.id));
      showToast('success', '테넌트가 삭제되었습니다.');
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      showToast('error', msg || '삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const statusBadge = (status: Tenant['status']) =>
    status === 'active'
      ? <Badge color="success" shape="tag">활성</Badge>
      : <Badge color="gray" shape="tag">비활성</Badge>;

  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="이름, 코드로 검색..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery('')}
      />
      <button className="users-search-refresh" onClick={loadTenants} disabled={loading} title="새로고침">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      </button>
    </div>
  );

  return (
    <>
      {!embedded && (
        <Topbar title="테넌트 관리" description="테넌트(상위 조직)를 관리합니다" />
      )}

      <div className={embedded ? '' : 'page-content'}>
        {embedded && (
          <div className="admin-panel-head">
            <PanelTitle title="테넌트 관리" description="테넌트(상위 조직)를 관리합니다." />
            <div className="admin-panel-actions">
              {tenants.length > 0 && searchBar}
              <Button color="primary" onClick={() => setShowAddModal(true)}>+ 테넌트 등록</Button>
            </div>
          </div>
        )}
        <div className={embedded ? 'admin-section' : 'page-content-inner'}>
          {!embedded && (
            <div className="admin-panel-actions" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
              {tenants.length > 0 && searchBar}
              <Button color="primary" onClick={() => setShowAddModal(true)}>+ 테넌트 등록</Button>
            </div>
          )}

          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th><SortableTh sort={sort} col="name">이름</SortableTh></th>
                    <th><SortableTh sort={sort} col="code">코드</SortableTh></th>
                    <th><SortableTh sort={sort} col="description">설명</SortableTh></th>
                    <th><SortableTh sort={sort} col="customerCount">고객사 수</SortableTh></th>
                    <th><SortableTh sort={sort} col="createdAt">등록일</SortableTh></th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="table-empty">
                        {searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다.` : '등록된 테넌트가 없습니다. 우측 상단 "테넌트 등록"으로 추가하세요.'}
                      </td>
                    </tr>
                  )}
                  {sort.sortRows(filtered, (t, key) =>
                    key === 'customerCount' ? customerCounts[t.id] || 0 : t[key] || ''
                  ).map((t) => (
                    <tr key={t.id}>
                      <td className="left">
                        <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); openDetail(t); }}>
                          {highlightText(t.name)}
                        </a>
                      </td>
                      <td className="left">{highlightText(t.code)}</td>
                      <td>{t.description || '-'}</td>
                      <td>{customerCounts[t.id] || 0}</td>
                      <td>{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-'}</td>
                      <td>
                        <div className="user-actions">
                          <button className="icon-action-btn" onClick={() => openDetail(t)} title="수정"><EditIcon /></button>
                          <button className="icon-action-btn delete" onClick={() => handleDelete(t.id, t.name)} title="삭제"><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 상세/수정 모달 */}
      <Modal
        isOpen={!!selected}
        onClose={closeDetail}
        title="테넌트 상세 정보"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeDetail}>취소</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
          </>
        }
      >
        {selected && (
          <div className="form-grid">
            <div className="form-item">
              <div className="form-label">이름</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">코드</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.code}
                  onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">설명</div>
              <div className="input">
                <input className="input-control" type="text" value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
            </div>
            <p className="user-detail-meta">
              ID {selected.id}
              <br />
              등록 {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '-'}
            </p>
          </div>
        )}
      </Modal>

      {/* 등록 모달 */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="테넌트 등록"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeAddModal}>취소</Button>
            <Button color="primary" onClick={handleAdd} disabled={adding}>{adding ? '등록 중...' : '등록'}</Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-item">
            <div className="form-label">이름 <span className="required">*</span></div>
            <div className={`input${addErrors.has('name') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="테넌트 이름"
                value={addForm.name} onChange={(e) => { setAddForm({ ...addForm, name: e.target.value }); clearAddError('name'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">코드 <span className="required">*</span></div>
            <div className={`input${addErrors.has('code') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="예: etech"
                value={addForm.code} onChange={(e) => { setAddForm({ ...addForm, code: e.target.value }); clearAddError('code'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">설명</div>
            <div className="input">
              <input className="input-control" type="text" placeholder="설명 (선택)"
                value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>

      {/* 테넌트 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="테넌트 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDelete} disabled={deleting}>삭제</Button>
          </>
        }
      >
        <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
          "{deleteConfirm?.name}" 테넌트를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>
    </>
  );
}

export default TenantsPage;
